import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-354">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-355">
        <header className="mb-12" data-id="element-356">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-357">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-358" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-359">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-360">
              <Shield className="w-8 h-8" data-id="element-361" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-362">
              Privacy Policy
            </h1>
          </div>
          <p className="text-slate-500 text-sm" data-id="element-363">Last updated: April 7, 2025</p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-364">
          <section data-id="element-365">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-366">
              1. Introduction
            </h2>
            <p data-id="element-367">
              Welcome to IntelliEats ("we," "our," or "us"). We are committed to
              protecting your personal information and your right to privacy.
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our mobile application
              IntelliEats (the "App").
            </p>
          </section>

          <section data-id="element-368">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-369">
              2. Information We Collect
            </h2>
            <div className="space-y-4" data-id="element-370">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-371">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-372">
                  Camera & Photo Access
                </h3>
                <p className="m-0" data-id="element-373">
                  We access your device camera and photo gallery solely to scan
                  ingredient labels. Images are processed locally on your device
                  for analysis and are not stored
                  permanently.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-374">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-375">Usage Data</h3>
                <p className="m-0" data-id="element-376">
                  We may collect anonymous usage data such as app interactions,
                  scan frequency, and feature usage to improve the App's
                  performance and user experience.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-377">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-378">
                  Device Information
                </h3>
                <p className="m-0" data-id="element-379">
                  We may collect device type, operating system version, and
                  unique device identifiers for analytics and troubleshooting
                  purposes.
                </p>
              </div>
            </div>
          </section>

          <section data-id="element-380">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-381">
              3. How We Use Your Information
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-382">
              <li className="flex items-start gap-3" data-id="element-383">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-384"></span>
                <span data-id="element-385">
                  To provide and maintain the ingredient scanning and analysis
                  service
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-386">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-387"></span>
                <span data-id="element-388">
                  To improve, personalize, and expand our App's features
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-389">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-390"></span>
                <span data-id="element-391">To understand and analyze how you use our App</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-392">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-393"></span>
                <span data-id="element-394">
                  To communicate with you for customer service and updates
                </span>
              </li>
            </ul>
          </section>

          <section data-id="element-395">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-396">
              4. Data Sharing & Third Parties
            </h2>
            <p data-id="element-397">
              We do not sell, trade, or rent your personal information to third
              parties. We may share anonymized, aggregated data with analytics
              partners to improve our services. Any third-party services used
              (such as analytics tools) are bound by their own privacy policies.
            </p>
          </section>

          <section data-id="element-398">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-399">
              5. Data Security
            </h2>
            <p data-id="element-400">
              We implement appropriate technical and organizational security
              measures to protect your personal information. However, no method
              of transmission over the Internet or electronic storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section data-id="element-401">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-402">
              6. Children's Privacy
            </h2>
            <p data-id="element-403">
              Our App is rated for ages 4+ and does not knowingly collect
              personal information from children under 13. If you believe we
              have collected information from a child under 13, please contact
              us immediately.
            </p>
          </section>

          <section data-id="element-404">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-405">
              7. Changes to This Policy
            </h2>
            <p data-id="element-406">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-407">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-408">
              8. Contact Us
            </h2>
            <p className="mb-4" data-id="element-409">
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <p className="font-medium text-slate-900" data-id="element-410">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-411">
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-412">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-413">
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}